/**
 * Customer-facing "Collabora" contractor page.
 *
 * Lives on per-sede domains (verdevercelli.it / verdevalsesia.it / …). The
 * backend builds links as `https://<sede-domain>/collabora/<token>`, so this
 * page is the sede-domain side of that contract.
 *
 * Same same-origin trick as SharePreventivoPage / ConfirmSlotPage: the calls
 * to `/api/public/collabora/<token>/…` are same-origin from the browser's POV;
 * the sede's nginx reverse-proxies them to `https://avmg.it/...` server-to-
 * server. No CORS, no cross-origin cookie surface, the brand experience stays
 * on the sede domain. The token in the URL IS the auth → `credentials: 'omit'`.
 *
 * No router is mounted — landings/ is a single-page marketing app. App.tsx
 * does a one-shot pathname check and renders this component when the URL
 * matches `^/collabora/<token>/?$`.
 */
import { useEffect, useId, useRef, useState } from 'react'

type CollaboraImage = {
  ref: string
  url: string
  thumb_url: string
  caption: string
}

type CollaboraInfo = {
  org_name: string
  org_tagline: string
  title: string
  description: string
  plant_details: string
  comune: string
  contractor_email: string
  expires_at: string
  submissions_used: number
  submissions_max: number
  images: CollaboraImage[]
}

type AvailabilityRow = { date: string; start: string; end: string }

// ── Pure helpers (module scope) ───────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const emptyRow = (): AvailabilityRow => ({ date: '', start: '', end: '' })

/** Local-timezone YYYY-MM-DD — the `min` on the date inputs. Backend re-checks. */
function todayISO(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// The backend rejects availability more than 365 days out — mirror the bound.
function maxAvailISO(): string {
  const d = new Date()
  d.setDate(d.getDate() + 365)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

// A native date input lets the year segment grow past 4 digits ("20000-…"),
// which `min`/`max` don't stop at the keystroke level. Refuse those values so
// they can't even be written; mid-typing states ("0002-…") are zero-padded to
// 4 digits by the browser and stay accepted.
function sane4DigitYear(v: string): boolean {
  return v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v)
}

// On blur, snap an out-of-range date back inside [today, today+365d].
function clampAvailDate(v: string): string {
  if (!v) return v
  const lo = todayISO()
  const hi = maxAvailISO()
  return v < lo ? lo : v > hi ? hi : v
}

/** Keep only digits + a single decimal separator (comma or dot), capping the
 *  fractional part at 2 digits. */
function sanitizeCost(raw: string): string {
  let s = raw.replace(/[^\d.,]/g, '')
  const firstSep = s.search(/[.,]/)
  if (firstSep !== -1) {
    const head = s.slice(0, firstSep)
    const sep = s[firstSep]
    const tail = s.slice(firstSep + 1).replace(/[.,]/g, '').slice(0, 2)
    s = head + sep + tail
  }
  return s
}

const COST_NAV_KEYS = new Set([
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
])

export default function CollaboraPage({ token }: { token: string }) {
  const [info, setInfo] = useState<CollaboraInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)

  const [email, setEmail] = useState('')
  const [rows, setRows] = useState<AvailabilityRow[]>([emptyRow()])
  const [cost, setCost] = useState('')
  const [website, setWebsite] = useState('') // honeypot — stays empty
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; cost?: string; availability?: string }>({})
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({})
  const [bannerError, setBannerError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [limitReached, setLimitReached] = useState(false)

  const [lightbox, setLightbox] = useState<number | null>(null)
  const emailPrefilled = useRef(false)

  // ── One-shot info load (logs the access) ──
  useEffect(() => {
    if (!token) return
    let cancelled = false
    fetch(`/api/public/collabora/${token}/`, { credentials: 'omit' })
      .then(async r => {
        if (cancelled) return
        if (r.status === 410) { setExpired(true); return }
        if (r.status === 404) { setError('Link non valido.'); return }
        if (!r.ok) { setError('Impossibile caricare la proposta.'); return }
        const data = (await r.json()) as CollaboraInfo
        setInfo(data)
        if (!emailPrefilled.current && data.contractor_email) {
          setEmail(data.contractor_email)
          emailPrefilled.current = true
        }
        if (data.submissions_used >= data.submissions_max) setLimitReached(true)
      })
      .catch(() => { if (!cancelled) setError('Impossibile caricare la proposta.') })
    return () => { cancelled = true }
  }, [token])

  const expiresAtLabel = info?.expires_at
    ? new Date(info.expires_at).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
    : null

  // ── Cost input hard-filtering ──
  const onCostKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return
    if (COST_NAV_KEYS.has(e.key)) return
    if (e.key.length !== 1) return
    const el = e.currentTarget
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    const isDigit = e.key >= '0' && e.key <= '9'
    const isSep = e.key === ',' || e.key === '.'
    if (!isDigit && !isSep) { e.preventDefault(); return }
    const next = el.value.slice(0, start) + e.key + el.value.slice(end)
    const seps = (next.match(/[.,]/g) || []).length
    if (seps > 1) { e.preventDefault(); return }
    const sepIdx = next.search(/[.,]/)
    if (sepIdx !== -1 && next.slice(sepIdx + 1).length > 2) { e.preventDefault() }
  }

  const onCostPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const el = e.currentTarget
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    const pasted = e.clipboardData.getData('text')
    const merged = el.value.slice(0, start) + pasted + el.value.slice(end)
    setCost(sanitizeCost(merged))
    if (fieldErrors.cost) setFieldErrors(p => ({ ...p, cost: undefined }))
  }

  // ── Availability row helpers ──
  const updateRow = (i: number, patch: Partial<AvailabilityRow>) => {
    setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
    if (rowErrors[i]) setRowErrors(prev => { const n = { ...prev }; delete n[i]; return n })
    if (fieldErrors.availability) setFieldErrors(p => ({ ...p, availability: undefined }))
  }
  const addRow = () => setRows(prev => (prev.length >= 10 ? prev : [...prev, emptyRow()]))
  const removeRow = (i: number) => {
    setRows(prev => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)))
    setRowErrors({})
  }

  // ── Client-side validation ──
  const validate = (): boolean => {
    const errs: typeof fieldErrors = {}
    const rErrs: Record<number, string> = {}

    if (!EMAIL_RE.test(email.trim())) errs.email = 'Inserisci un indirizzo email valido.'

    const flags = rows.map(r => ({
      any: Boolean(r.date || r.start || r.end),
      all: Boolean(r.date && r.start && r.end),
    }))
    const complete = rows.filter((_, i) => flags[i].all)
    const hasPartial = flags.some(f => f.any && !f.all)

    if (complete.length === 0) {
      errs.availability = 'Aggiungi almeno una fascia di disponibilità completa (data, ora inizio e ora fine).'
    } else if (hasPartial) {
      errs.availability = 'Completa o rimuovi le fasce con campi mancanti.'
    }

    rows.forEach((r, i) => {
      if (flags[i].all && r.start >= r.end) {
        rErrs[i] = "L'ora di inizio deve precedere quella di fine."
      }
    })

    const seen = new Set<string>()
    for (let i = 0; i < rows.length; i++) {
      if (!flags[i].all) continue
      const key = `${rows[i].date}|${rows[i].start}|${rows[i].end}`
      if (seen.has(key)) errs.availability = 'Ci sono fasce di disponibilità duplicate.'
      seen.add(key)
    }

    const costNum = Number(cost.trim().replace(',', '.'))
    if (!cost.trim() || Number.isNaN(costNum) || costNum <= 0) {
      errs.cost = "Inserisci un'offerta valida maggiore di zero."
    }

    setFieldErrors(errs)
    setRowErrors(rErrs)
    return Object.keys(errs).length === 0 && Object.keys(rErrs).length === 0
  }

  const applyBackendErrors = (body: unknown) => {
    const b = (body ?? {}) as Record<string, unknown>
    const pick = (v: unknown): string | undefined =>
      Array.isArray(v) ? String(v[0]) : typeof v === 'string' ? v : undefined
    const errs: typeof fieldErrors = {}
    if (b.email) errs.email = pick(b.email)
    if (b.cost) errs.cost = pick(b.cost)
    if (b.availability) errs.availability = pick(b.availability)
    const generic: string[] = []
    const w = pick(b.website); if (w) generic.push(w)
    const d = pick(b.detail); if (d) generic.push(d)
    setFieldErrors(errs)
    if (generic.length) setBannerError(generic.join(' '))
    else if (!errs.email && !errs.cost && !errs.availability) setBannerError('Controlla i campi inseriti e riprova.')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setBannerError(null)
    if (!validate()) return
    setSubmitting(true)
    const availability = rows
      .filter(r => r.date && r.start && r.end)
      .map(r => ({ date: r.date, start: r.start, end: r.end }))
    try {
      const res = await fetch(`/api/public/collabora/${token}/submit/`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: email.trim(), availability, cost: cost.trim(), website }),
      })
      if (res.status === 201) { setSubmitted(true); return }
      if (res.status === 410) { setExpired(true); return }
      if (res.status === 429) {
        setBannerError('Limite proposte raggiunto o troppi tentativi — riprova più tardi.')
        return
      }
      if (res.status === 400) {
        const body = await res.json().catch(() => ({}))
        applyBackendErrors(body)
        return
      }
      setBannerError('Si è verificato un errore. Riprova più tardi.')
    } catch {
      setBannerError('Errore di rete. Controlla la connessione e riprova.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'block w-full py-2.5 px-3.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 outline-none transition-all duration-300 focus:border-[#013220] focus:ring-2 focus:ring-[#013220]/20 hover:border-[#013220]/30'

  return (
    <div className="relative min-h-screen overflow-hidden px-4 py-10 bg-gradient-to-br from-emerald-900 via-[#0a3a26] to-emerald-950">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)' }}
        aria-hidden="true"
      />
      <BotanicalBackdrop />

      <div className="relative z-10 w-full mx-auto max-w-lg lg:max-w-5xl">
        {/* Header */}
        {info && (
          <div className="text-center mb-7">
            <p className="text-sm sm:text-base uppercase tracking-[0.3em] font-semibold text-emerald-100">
              {info.org_name}
            </p>
            {info.org_tagline && (
              <p className="mt-1 text-[11px] uppercase tracking-[0.25em] font-medium text-emerald-200/70">
                {info.org_tagline}
              </p>
            )}
          </div>
        )}

        {/* ── Expired (410) ── */}
        {expired && (
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl shadow-black/40 px-8 py-12 text-center mx-auto max-w-lg">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-base font-semibold text-gray-800">Link scaduto</p>
            <p className="text-sm text-gray-500 mt-1.5 max-w-xs mx-auto">
              Questo link non è più valido. Per ricevere un nuovo link, ci contatti.
            </p>
          </div>
        )}

        {/* ── Not found / network error ── */}
        {error && !expired && (
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl shadow-black/40 px-8 py-12 text-center mx-auto max-w-lg">
            <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p className="text-base font-semibold text-gray-800">{error === 'Link non valido.' ? 'Link non valido' : 'Errore'}</p>
            <p className="text-sm text-gray-500 mt-1.5 max-w-xs mx-auto">
              {error === 'Link non valido.'
                ? 'Il link potrebbe non essere corretto o non essere più disponibile.'
                : 'Non è stato possibile caricare la proposta. Controlli la connessione e riprovi.'}
            </p>
            {error !== 'Link non valido.' && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#013220] hover:bg-[#016432] px-4 py-2.5 text-sm font-semibold text-white transition-colors"
              >
                Riprova
              </button>
            )}
          </div>
        )}

        {/* ── Loading skeleton ── */}
        {!info && !error && !expired && (
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl shadow-black/40 px-6 py-16 flex items-center justify-center mx-auto max-w-lg">
            <div className="w-6 h-6 border-2 border-emerald-700/30 border-t-emerald-700 rounded-full animate-spin" />
          </div>
        )}

        {/* ── Content ── */}
        {info && !expired && (
          <div className="space-y-6 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0 lg:items-start">
            {/* Proposal card */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl shadow-black/40 p-7">
              <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-700/70 font-bold">Proposta di collaborazione</p>
              <h1 className="mt-1.5 text-2xl font-bold text-gray-900 tracking-tight leading-snug">{info.title}</h1>

              {/* Gallery */}
              {info.images.length > 0 && (
                <div className="mt-5 grid grid-cols-3 gap-2">
                  {info.images.map((img, i) => (
                    <button
                      key={img.ref}
                      type="button"
                      onClick={() => setLightbox(i)}
                      className="group relative aspect-square overflow-hidden rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#013220]"
                      aria-label={img.caption || `Foto ${i + 1}`}
                    >
                      <img
                        src={img.thumb_url}
                        alt={img.caption || `Foto ${i + 1}`}
                        title={img.caption || undefined}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Description */}
              {info.description && (
                <p className="mt-5 text-[15px] leading-relaxed text-gray-700 whitespace-pre-line">{info.description}</p>
              )}

              {/* Dettagli */}
              {info.plant_details && (
                <div className="mt-5 pt-5 border-t border-dashed border-gray-200">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-700/70 font-bold">Dettagli</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-700 whitespace-pre-line">{info.plant_details}</p>
                </div>
              )}

              {/* Località */}
              {info.comune && (
                <p className="mt-5 flex items-center gap-1.5 text-sm text-gray-600">
                  <span aria-hidden="true">📍</span>
                  <span className="font-medium">{info.comune}</span>
                </p>
              )}

              {expiresAtLabel && (
                <p className="mt-5 text-[11px] text-gray-400">Link valido fino al {expiresAtLabel}</p>
              )}
            </div>

            {/* Form card / success / limit */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl shadow-black/40 p-7 lg:sticky lg:top-8">
              {submitted ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-4">
                    <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <p className="text-lg font-bold text-gray-900">Proposta inviata ✅</p>
                  <p className="text-sm text-gray-500 mt-1.5 max-w-xs mx-auto">
                    Riceverai una email di conferma a <span className="font-semibold text-gray-700">{email.trim()}</span>.
                  </p>
                </div>
              ) : limitReached ? (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-4 text-center">
                  <p className="text-sm font-medium text-amber-800">
                    Hai già inviato il numero massimo di proposte per questo link.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-700/70 font-bold">La tua proposta</p>
                    <p className="mt-1 text-sm text-gray-500">Indichi la sua disponibilità e la sua offerta.</p>
                  </div>

                  {/* Honeypot — off-screen, always sent */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                    <label htmlFor="collabora-website">Website</label>
                    <input
                      id="collabora-website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={e => setWebsite(e.target.value)}
                    />
                  </div>

                  {bannerError && (
                    <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                      <p className="text-sm text-red-600 font-medium">{bannerError}</p>
                    </div>
                  )}

                  {/* Email */}
                  <div>
                    <label htmlFor="collabora-email" className="block mb-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      La tua email (riceverai la conferma qui)
                    </label>
                    <input
                      id="collabora-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors(p => ({ ...p, email: undefined })) }}
                      className={`${inputCls} ${fieldErrors.email ? 'border-red-400' : ''}`}
                    />
                    {fieldErrors.email && <p className="text-red-500 text-[11px] mt-1">{fieldErrors.email}</p>}
                  </div>

                  {/* Disponibilità */}
                  <div>
                    <label className="block mb-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">Disponibilità</label>
                    <div className="space-y-3">
                      {rows.map((row, i) => (
                        <div key={i} className={i > 0 ? 'pt-3 border-t border-gray-100' : ''}>
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              aria-label="Data"
                              min={todayISO()}
                              max={maxAvailISO()}
                              value={row.date}
                              onChange={e => { if (sane4DigitYear(e.target.value)) updateRow(i, { date: e.target.value }) }}
                              onBlur={e => {
                                const c = clampAvailDate(e.target.value)
                                if (c !== e.target.value) updateRow(i, { date: c })
                              }}
                              className={`${inputCls} flex-1 min-w-0 px-2.5 ${rowErrors[i] ? 'border-red-400' : ''}`}
                            />
                            {rows.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRow(i)}
                                aria-label="Rimuovi fascia"
                                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <div>
                              <span className="block text-[9px] uppercase tracking-wide text-gray-400 mb-0.5">dalle</span>
                              <input
                                type="time"
                                aria-label="Ora inizio"
                                max={row.end || undefined}
                                value={row.start}
                                onChange={e => updateRow(i, { start: e.target.value })}
                                onBlur={() => { if (row.start && row.end && row.start >= row.end) updateRow(i, { start: '' }) }}
                                className={`${inputCls} px-2 text-center ${rowErrors[i] ? 'border-red-400' : ''}`}
                              />
                            </div>
                            <div>
                              <span className="block text-[9px] uppercase tracking-wide text-gray-400 mb-0.5">alle</span>
                              <input
                                type="time"
                                aria-label="Ora fine"
                                min={row.start || undefined}
                                value={row.end}
                                onChange={e => updateRow(i, { end: e.target.value })}
                                onBlur={() => { if (row.start && row.end && row.end <= row.start) updateRow(i, { end: '' }) }}
                                className={`${inputCls} px-2 text-center ${rowErrors[i] ? 'border-red-400' : ''}`}
                              />
                            </div>
                          </div>
                          {rowErrors[i] && <p className="text-red-500 text-[11px] mt-1.5">{rowErrors[i]}</p>}
                        </div>
                      ))}
                    </div>
                    {fieldErrors.availability && <p className="text-red-500 text-[11px] mt-1.5">{fieldErrors.availability}</p>}
                    {rows.length < 10 && (
                      <button
                        type="button"
                        onClick={addRow}
                        className="mt-2.5 inline-flex items-center gap-1 text-sm font-semibold text-[#013220] hover:text-[#016432] transition-colors"
                      >
                        <span aria-hidden="true">+</span> Aggiungi data
                      </button>
                    )}
                  </div>

                  {/* Costo */}
                  <div>
                    <label htmlFor="collabora-cost" className="block mb-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      La tua offerta
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
                      <input
                        id="collabora-cost"
                        name="cost"
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        placeholder="0,00"
                        value={cost}
                        onKeyDown={onCostKeyDown}
                        onPaste={onCostPaste}
                        onChange={e => { setCost(sanitizeCost(e.target.value)); if (fieldErrors.cost) setFieldErrors(p => ({ ...p, cost: undefined })) }}
                        className={`${inputCls} pl-8 ${fieldErrors.cost ? 'border-red-400' : ''}`}
                      />
                    </div>
                    {fieldErrors.cost && <p className="text-red-500 text-[11px] mt-1">{fieldErrors.cost}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="group w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl bg-gradient-to-r from-[#013220] to-emerald-700 hover:from-[#016432] hover:to-emerald-600 text-white font-semibold shadow-lg shadow-emerald-900/30 transition-all hover:shadow-xl hover:shadow-emerald-900/40 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
                  >
                    {submitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Invio in corso…
                      </>
                    ) : 'Invia la proposta'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {info && (
          <div className="mt-8 text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-200/50">Powered by Automazione Verde</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {info && lightbox !== null && info.images[lightbox] && (
        <Lightbox
          images={info.images}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onIndex={setLightbox}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Dependency-free fullscreen lightbox — dark backdrop, prev/next, ✕,
// Esc + arrow keys, backdrop-click to close.
// ────────────────────────────────────────────────────────────────────

function Lightbox({
  images, index, onClose, onIndex,
}: {
  images: CollaboraImage[]
  index: number
  onClose: () => void
  onIndex: (i: number) => void
}) {
  const many = images.length > 1
  const go = (delta: number) => onIndex((index + delta + images.length) % images.length)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft' && many) go(-1)
      else if (e.key === 'ArrowRight' && many) go(1)
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [index, images.length])

  const current = images[index]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={current.caption || 'Anteprima foto'}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Chiudi"
        className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {many && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); go(-1) }}
          aria-label="Foto precedente"
          className="absolute left-3 sm:left-6 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      <figure className="max-w-full max-h-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
        <img
          src={current.url}
          alt={current.caption || `Foto ${index + 1}`}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
        />
        {current.caption && (
          <figcaption className="mt-3 text-sm text-white/80 text-center max-w-xl">{current.caption}</figcaption>
        )}
        {many && (
          <p className="mt-2 text-xs text-white/50">{index + 1} / {images.length}</p>
        )}
      </figure>

      {many && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); go(1) }}
          aria-label="Foto successiva"
          className="absolute right-3 sm:right-6 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────
// Botanical decorations — same trimmed set as SharePreventivoPage.
// ────────────────────────────────────────────────────────────────────

function BotanicalBackdrop() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <TropicalLeaf className="absolute -top-12 -right-24 w-[22rem] h-[34rem] text-emerald-300 opacity-[0.28] rotate-[18deg]" />
      <WideLeaf className="absolute -bottom-20 -left-16 w-[24rem] h-[24rem] text-emerald-300 opacity-[0.25] rotate-[28deg]" />
      <Daisy className="absolute top-[6%] left-2 sm:left-4 w-24 h-24 sm:w-28 sm:h-28 text-emerald-100 opacity-[0.28] rotate-[12deg]" />
      <Daisy className="hidden md:block absolute top-[70%] right-10 w-24 h-24 text-emerald-100 opacity-[0.26] -rotate-[18deg]" />
      <Daisy className="hidden md:block absolute top-[38%] left-6 w-20 h-20 text-emerald-100 opacity-[0.24] rotate-[24deg]" />
    </div>
  )
}

function TropicalLeaf({ className = '' }: { className?: string }) {
  const id = useId().replace(/:/g, '')
  const bodyPath = 'M124 8 C 188 28, 224 100, 224 184 C 222 264, 184 326, 122 350 C 60 326, 22 264, 20 184 C 20 100, 60 28, 124 8 Z'
  return (
    <svg viewBox="0 0 240 360" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={`tl-${id}`} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.45" />
          <stop offset="60%" stopColor="currentColor" stopOpacity="0.9" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
        </linearGradient>
        <clipPath id={`tl-clip-${id}`}>
          <path d={bodyPath} />
        </clipPath>
      </defs>
      <path fill={`url(#tl-${id})`} d={bodyPath} />
      <path
        fill="currentColor" fillOpacity="0.18"
        d="M124 36 C 174 54, 202 110, 202 186 C 200 254, 170 304, 122 322 C 74 304, 44 254, 42 186 C 42 110, 74 54, 124 36 Z"
      />
      <g clipPath={`url(#tl-clip-${id})`}>
        <path
          fill="none" stroke="currentColor" strokeOpacity="0.7" strokeWidth="2.2" strokeLinecap="round"
          d="M124 22 C 126 130, 120 240, 122 338"
        />
      </g>
    </svg>
  )
}

function WideLeaf({ className = '' }: { className?: string }) {
  const id = useId().replace(/:/g, '')
  const bodyPath = 'M140 14 C 80 22, 32 70, 24 132 C 20 200, 60 264, 140 288 C 220 264, 260 200, 256 132 C 248 70, 200 22, 140 14 Z'
  return (
    <svg viewBox="0 0 280 300" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={`wl-${id}`} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.45" />
          <stop offset="60%" stopColor="currentColor" stopOpacity="0.9" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
        </linearGradient>
        <clipPath id={`wl-clip-${id}`}>
          <path d={bodyPath} />
        </clipPath>
      </defs>
      <path fill={`url(#wl-${id})`} d={bodyPath} />
      <path
        fill="currentColor" fillOpacity="0.18"
        d="M140 42 C 92 48, 54 90, 48 138 C 46 196, 82 248, 140 264 C 198 248, 234 196, 232 138 C 226 90, 188 48, 140 42 Z"
      />
      <g clipPath={`url(#wl-clip-${id})`}>
        <path
          fill="none" stroke="currentColor" strokeOpacity="0.7" strokeWidth="2.2" strokeLinecap="round"
          d="M140 26 C 138 100, 142 200, 140 280"
        />
      </g>
    </svg>
  )
}

function Daisy({ className = '' }: { className?: string }) {
  const id = useId().replace(/:/g, '')
  const angles = Array.from({ length: 14 }, (_, i) => i * (360 / 14))
  return (
    <svg viewBox="0 0 200 200" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={`dz-${id}`} x1="50%" y1="100%" x2="50%" y2="0%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.55" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
        </linearGradient>
      </defs>
      <g fill={`url(#dz-${id})`}>
        {angles.map(a => (
          <path
            key={a}
            transform={`rotate(${a} 100 100)`}
            d="M 100 100 C 94 80, 94 38, 100 18 C 106 38, 106 80, 100 100 Z"
          />
        ))}
      </g>
      <circle cx="100" cy="100" r="14" fill="currentColor" fillOpacity="0.95" />
    </svg>
  )
}
